import { useState } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { useForm, useFieldArray } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useTranslation } from "react-i18next"
import { ArrowLeft, Plus, Trash2, Search, Loader2, AlertTriangle } from "lucide-react"
import { useCompany, useCreateCompany, useUpdateCompany, useNipLookup } from "@/hooks/use-companies"
import { cn } from "@/lib/utils"
import { ApiClientError } from "@/lib/api-client"
import { toast } from "sonner"

const companySchema = z.object({
  name: z.string().min(1, "Nazwa jest wymagana"),
  short_name: z.string().optional(),
  nip: z.string().optional(),
  regon: z.string().optional(),
  krs: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  postal_code: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  website: z.string().optional(),
  description: z.string().optional(),
  emails: z.array(z.object({
    email: z.string().email("Nieprawidłowy email"),
    is_primary: z.boolean(),
    label: z.string().optional(),
  })),
  phones: z.array(z.object({
    number: z.string().min(1, "Numer jest wymagany"),
    is_primary: z.boolean(),
    label: z.string().optional(),
  })),
})

type CompanyForm = z.infer<typeof companySchema>

export function CompanyFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id
  const [error, setError] = useState<string | null>(null)

  const { data: companyData } = useCompany(id)
  const createMutation = useCreateCompany()
  const updateMutation = useUpdateCompany()

  const nipLookup = useNipLookup()
  const [nipDuplicate, setNipDuplicate] = useState<{ id: string; name: string } | null>(null)

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CompanyForm>({
    resolver: zodResolver(companySchema),
    values: isEdit && companyData ? {
      name: companyData.data.name,
      short_name: companyData.data.short_name || "",
      nip: companyData.data.nip || "",
      regon: companyData.data.regon || "",
      krs: companyData.data.krs || "",
      street: companyData.data.street || "",
      city: companyData.data.city || "",
      postal_code: companyData.data.postal_code || "",
      state: companyData.data.state || "",
      country: companyData.data.country || "PL",
      website: companyData.data.website || "",
      description: companyData.data.description || "",
      emails: companyData.data.emails.map((e) => ({
        email: e.email,
        is_primary: e.is_primary,
        label: e.label || "",
      })),
      phones: companyData.data.phones.map((p) => ({
        number: p.number,
        is_primary: p.is_primary,
        label: p.label || "",
      })),
    } : {
      name: "",
      emails: [],
      phones: [],
      country: "PL",
    },
  })

  const emailFields = useFieldArray({ control, name: "emails" })
  const phoneFields = useFieldArray({ control, name: "phones" })

  const nipValue = watch("nip")

  const handleNipLookup = () => {
    const nip = nipValue?.trim()
    if (!nip) {
      toast.error("Wpisz NIP aby wyszukać")
      return
    }

    setNipDuplicate(null)
    nipLookup.mutate(nip, {
      onSuccess: (res) => {
        const { gus_data, existing_company } = res.data

        if (existing_company) {
          setNipDuplicate(existing_company)
        }

        // Auto-fill form fields from GUS data
        if (gus_data.name) setValue("name", gus_data.name)
        if (gus_data.regon) setValue("regon", gus_data.regon)
        if (gus_data.street) setValue("street", gus_data.street)
        if (gus_data.city) setValue("city", gus_data.city)
        if (gus_data.postal_code) setValue("postal_code", gus_data.postal_code)
        if (gus_data.state) setValue("state", gus_data.state)
        if (gus_data.nip) setValue("nip", gus_data.nip)

        toast.success("Dane pobrane z rejestru GUS")
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : "Błąd wyszukiwania NIP")
      },
    })
  }

  const onSubmit = async (data: CompanyForm) => {
    try {
      setError(null)
      if (isEdit) {
        await updateMutation.mutateAsync({ id, data })
        toast.success("Kontrahent zaktualizowany")
      } else {
        await createMutation.mutateAsync(data)
        toast.success("Kontrahent utworzony")
      }
      navigate("/companies")
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError("Wystąpił nieoczekiwany błąd")
      }
    }
  }

  const inputClass = "h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm text-foreground outline-none"

  return (
    <div className="mx-auto max-w-[720px] space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/50"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-foreground">
          {isEdit ? "Edytuj kontrahenta" : t("companies.new")}
        </h1>
      </div>

      {error && (
        <div className="rounded-lg px-4 py-3 text-sm bg-error-light text-error-dark">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic info */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">
            Dane podstawowe
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">
                {t("companies.name")} *
              </label>
              <input
                {...register("name")}
                className={cn(inputClass, errors.name && "border-error")}
              />
              {errors.name && <p className="text-xs text-error">{errors.name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("companies.shortName")}</label>
              <input {...register("short_name")} className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("companies.nip")}</label>
              <div className="flex gap-2">
                <input
                  {...register("nip")}
                  placeholder="np. 5261040828"
                  className={`${inputClass} flex-1`}
                />
                <button
                  type="button"
                  onClick={handleNipLookup}
                  disabled={nipLookup.isPending}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-primary bg-primary/5 px-3 text-xs font-medium text-primary whitespace-nowrap transition-colors hover:opacity-80 disabled:opacity-50"
                  title="Pobierz dane z GUS"
                >
                  {nipLookup.isPending ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                  GUS
                </button>
              </div>
              {nipDuplicate && (
                <div className="flex items-center gap-2 rounded-md px-3 py-2 text-xs bg-warning-light text-warning-dark">
                  <AlertTriangle size={14} className="shrink-0" />
                  <span>
                    Kontrahent z tym NIP już istnieje:{" "}
                    <Link to={`/companies/${nipDuplicate.id}`} className="font-medium underline">
                      {nipDuplicate.name}
                    </Link>
                  </span>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("companies.regon")}</label>
              <input {...register("regon")} className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("companies.krs")}</label>
              <input {...register("krs")} className={inputClass} />
            </div>
          </div>
        </section>

        {/* Address */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">
            {t("companies.address")}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Ulica</label>
              <input {...register("street")} className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("companies.city")}</label>
              <input {...register("city")} className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("companies.postalCode")}</label>
              <input {...register("postal_code")} className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("companies.country")}</label>
              <input {...register("country")} className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("companies.website")}</label>
              <input {...register("website")} placeholder="https://" className={inputClass} />
            </div>
          </div>
        </section>

        {/* Emails */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase text-muted-foreground">Email</h2>
            <button type="button" onClick={() => emailFields.append({ email: "", is_primary: emailFields.fields.length === 0, label: "" })}
              className="inline-flex items-center gap-1 text-xs text-primary">
              <Plus size={14} /> Dodaj
            </button>
          </div>
          {emailFields.fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2">
              <input {...register(`emails.${index}.email`)} placeholder="email@example.com" className={cn(inputClass, "flex-1", errors.emails?.[index]?.email && "border-error")} />
              <input {...register(`emails.${index}.label`)} placeholder="Etykieta" className={`${inputClass} w-28`} />
              <button type="button" onClick={() => emailFields.remove(index)} className="p-1.5 text-muted-foreground">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </section>

        {/* Phones */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase text-muted-foreground">Telefon</h2>
            <button type="button" onClick={() => phoneFields.append({ number: "", is_primary: phoneFields.fields.length === 0, label: "" })}
              className="inline-flex items-center gap-1 text-xs text-primary">
              <Plus size={14} /> Dodaj
            </button>
          </div>
          {phoneFields.fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2">
              <input {...register(`phones.${index}.number`)} placeholder="+48..." className={cn(inputClass, "flex-1", errors.phones?.[index]?.number && "border-error")} />
              <input {...register(`phones.${index}.label`)} placeholder="Etykieta" className={`${inputClass} w-28`} />
              <button type="button" onClick={() => phoneFields.remove(index)} className="p-1.5 text-muted-foreground">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </section>

        {/* Description */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">{t("companies.description")}</h2>
          <textarea {...register("description")} rows={3}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none" />
        </section>

        {/* Submit */}
        <div className="flex justify-end gap-3 border-t border-border pt-4">
          <button type="button" onClick={() => navigate(-1)}
            className="h-9 rounded-md border border-input px-4 text-sm font-medium text-foreground transition-colors">
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={isSubmitting}
            className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors disabled:opacity-50">
            {isSubmitting ? t("common.loading") : t("common.save")}
          </button>
        </div>
      </form>
    </div>
  )
}
